import { Link, useLocation } from "wouter";
import {
  Menu,
  X,
  ChevronDown,
  Globe2,
  ScanFace,
  ShieldCheck,
  QrCode,
  Euro,
  Wrench,
  Users,
  BarChart3,
  Mail,
  Lock,
  CalendarRange,
  Puzzle,
  Code2,
  Building2,
  Video,
  Phone,
  DoorOpen,
  Cable,
  Headset,
  Map as MapIcon,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { StructureNavigation } from "@/components/StructureNavigation";

const BOOKING_URL = "https://gestionale.seosoft.it/book/consulenza-sostenibilit-aziendale";
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useLocale, localePath as buildLocalePath, LOCALE_LABELS, availableLocalesForPath, type Locale } from "@/lib/locale";
import { rememberLanguagePreference } from "@/components/LanguageSuggestion";

function CountryFlag({ locale }: { locale: Locale }) {
  return (
    <svg
      viewBox="0 0 24 16"
      className="h-4 w-6 shrink-0 overflow-hidden rounded-[2px] border border-black/10 shadow-sm"
      aria-hidden="true"
    >
      {locale === "it" && (
        <>
          <rect width="8" height="16" fill="#009246" />
          <rect x="8" width="8" height="16" fill="#fff" />
          <rect x="16" width="8" height="16" fill="#ce2b37" />
        </>
      )}
      {locale === "de" && (
        <>
          <rect width="24" height="5.34" fill="#000" />
          <rect y="5.33" width="24" height="5.34" fill="#dd0000" />
          <rect y="10.66" width="24" height="5.34" fill="#ffce00" />
        </>
      )}
      {locale === "hr" && (
        <>
          <rect width="24" height="5.34" fill="#ff0000" />
          <rect y="5.33" width="24" height="5.34" fill="#fff" />
          <rect y="10.66" width="24" height="5.34" fill="#171796" />
          <rect x="10" y="5" width="4" height="6" rx="0.5" fill="#fff" stroke="#d40000" strokeWidth="0.7" />
          <path d="M10.5 6h3M10.5 7.5h3M11.5 5.5v3M12.8 5.5v3" stroke="#d40000" strokeWidth="0.45" />
        </>
      )}
      {locale === "en" && (
        <>
          <rect width="24" height="16" fill="#012169" />
          <path d="M0 0 24 16M24 0 0 16" stroke="#fff" strokeWidth="4" />
          <path d="M0 0 24 16M24 0 0 16" stroke="#c8102e" strokeWidth="1.8" />
          <path d="M12 0v16M0 8h24" stroke="#fff" strokeWidth="5" />
          <path d="M12 0v16M0 8h24" stroke="#c8102e" strokeWidth="2.7" />
        </>
      )}
    </svg>
  );
}

function DesktopLanguagePicker({ locale, onChange, label, availableLocales }: { locale: Locale; onChange: (locale: Locale) => void; label: string; availableLocales: readonly Locale[] }) {
  return (
    <details className="relative group">
      <summary
        className="flex cursor-pointer list-none items-center gap-2 rounded-full px-2 py-1.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted [&::-webkit-details-marker]:hidden"
        aria-label={label}
      >
        <CountryFlag locale={locale} />
        <span>{LOCALE_LABELS[locale]}</span>
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="absolute right-0 top-full mt-2 min-w-44 overflow-hidden rounded-xl border border-border bg-background p-1.5 shadow-xl">
        {availableLocales.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            className={cn(
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted",
              item === locale && "bg-primary/5 font-semibold text-primary",
            )}
          >
            <CountryFlag locale={item} />
            {LOCALE_LABELS[item]}
          </button>
        ))}
      </div>
    </details>
  );
}

const featureGroups = [
  {
    label: "Prenotazioni e ricavi",
    icon: CalendarRange,
    items: [
      { href: "/prenotazioni-online", label: "Booking Engine", icon: Globe2, desc: "Prenotazioni dirette senza commissioni" },
      { href: "/sincronizzazione-ical", label: "Sincronizzazione iCal", icon: CalendarRange, desc: "Feed .ics per piazzola — Airbnb, Booking, Google Cal" },
      { href: "/prezzi-dinamici", label: "Prezzi Dinamici e Tasse", icon: Euro, desc: "Stagionalità e tassa di soggiorno automatica" },
      { href: "/reportistica", label: "Reportistica e Statistiche", icon: BarChart3, desc: "Dashboard presenze e PDF giornaliero" },
      { href: "/automazione-email", label: "Automazione Email", icon: Mail, desc: "Conferme, promemoria e notifiche SMTP" },
    ],
  },
  {
    label: "Ospiti e accessi",
    icon: ScanFace,
    items: [
      { href: "/check-in-digitale", label: "Check-in Digitale con AI", icon: ScanFace, desc: "OCR documenti e link magico self check-in" },
      { href: "/videoverifica", label: "OVVO Videoverifica", icon: Video, desc: "Check-in remoto via videochiamata con verbale legale" },
      { href: "/alloggiati-web", label: "Alloggiati Web (Questura)", icon: ShieldCheck, desc: "Invio automatico schedine ministeriali" },
      { href: "/check-out-qr-code", label: "Check-out con QR Code", icon: QrCode, desc: "Sbarra automatica e riepilogo soggiorno" },
      { href: "/controllo-accessi-sbarra", label: "Controllo Accessi Sbarra", icon: DoorOpen, desc: "Integrazione varchi e log presenze" },
      { href: "/come-collegare-gestionale-sbarre", label: "Come collegare il gestionale alle sbarre", icon: Cable, desc: "Hardware, schema di collegamento e sicurezza" },
      { href: "/reception-remota", label: "Reception Remota 24/7", icon: Headset, desc: "Ingressi, uscite e pratiche ospiti da remoto" },
    ],
  },
  {
    label: "Operatività e integrazioni",
    icon: Wrench,
    items: [
      { href: "/assistente-ai-campeggio", label: "Assistente operativo AI", icon: Headset, desc: "Guida a procedure e configurazioni" },
      { href: "/gestionale-aree-sosta-camper#automazione", label: "Gestionale Aree Sosta Camper", icon: MapIcon, desc: "Piazzole, prenotazioni e accessi" },
      { href: "/mappa-interattiva", label: "Mappa Interattiva", icon: MapIcon, desc: "Stato piazzole in tempo reale, drag & drop" },
      { href: "/gestione-manutenzioni", label: "Gestione Manutenzioni", icon: Wrench, desc: "Segnalazione guasti e blocco piazzole" },
      { href: "/ruoli-permessi", label: "Ruoli e Permessi Staff", icon: Users, desc: "Admin, Reception e Manutentore isolati" },
      { href: "/sicurezza-backup", label: "Sicurezza e Backup GDPR", icon: Lock, desc: "Crittografia, MFA e export JSON completo" },
      { href: "/integrazioni", label: "Integrazioni Personalizzate", icon: Puzzle, desc: "Sbarre, ANPR, EV, serrature smart, IoT e altro" },
    ],
  },
];

type FeatureItem = (typeof featureGroups)[number]["items"][number];
type FeatureKey = Exclude<FeatureItem["label"], "Reception Remota 24/7">;
type NavItemCopy = { label: string; desc: string };
type NavCopy = {
  features: string; prices: string; blog: string; updates: string; company: string; contacts: string;
  free: string; demo: string; language: string; open: string; close: string;
  groups: Record<string, string>; items: Record<FeatureKey, NavItemCopy>;
  allFeatures: string; allDetails: string; team: string; teamDesc: string;
  technology: string; technologyDesc: string;
};

const navCopies: Record<Locale, NavCopy> = {
  it: {
    features: "Funzionalità", prices: "Prezzi", blog: "Blog", updates: "Novità", company: "Azienda", contacts: "Contatti", free: "Consulenza gratuita", demo: "Richiedi Demo", language: "Lingua", open: "Apri menu", close: "Chiudi menu",
    groups: { "Prenotazioni e ricavi": "Prenotazioni e ricavi", "Ospiti e accessi": "Ospiti e accessi", "Operatività e integrazioni": "Operatività e integrazioni" },
    items: {
      "Booking Engine": { label: "Booking Engine", desc: "Prenotazioni dirette senza commissioni" }, "Sincronizzazione iCal": { label: "Sincronizzazione iCal", desc: "Feed .ics per piazzola — Airbnb, Booking, Google Cal" }, "Check-in Digitale con AI": { label: "Check-in Digitale con AI", desc: "OCR documenti e link magico self check-in" }, "OVVO Videoverifica": { label: "OVVO Videoverifica", desc: "Check-in remoto via videochiamata con verbale legale" }, "Alloggiati Web (Questura)": { label: "Alloggiati Web (Questura)", desc: "Invio automatico schedine ministeriali" }, "Check-out con QR Code": { label: "Check-out con QR Code", desc: "Sbarra automatica e riepilogo soggiorno" }, "Controllo Accessi Sbarra": { label: "Controllo Accessi Sbarra", desc: "Integrazione varchi e log presenze" }, "Gestionale Aree Sosta Camper": { label: "Gestionale Aree Sosta Camper", desc: "Piazzole, prenotazioni e accessi" }, "Mappa Interattiva": { label: "Mappa Interattiva", desc: "Stato piazzole in tempo reale, drag & drop" }, "Prezzi Dinamici e Tasse": { label: "Prezzi Dinamici e Tasse", desc: "Stagionalità e tassa di soggiorno automatica" }, "Gestione Manutenzioni": { label: "Gestione Manutenzioni", desc: "Segnalazione guasti e blocco piazzole" }, "Ruoli e Permessi Staff": { label: "Ruoli e Permessi Staff", desc: "Admin, Reception e Manutentore isolati" }, "Reportistica e Statistiche": { label: "Reportistica e Statistiche", desc: "Dashboard presenze e PDF giornaliero" }, "Automazione Email": { label: "Automazione Email", desc: "Conferme, promemoria e notifiche SMTP" }, "Sicurezza e Backup GDPR": { label: "Sicurezza e Backup GDPR", desc: "Crittografia, MFA e export JSON completo" }, "Integrazioni Personalizzate": { label: "Integrazioni Personalizzate", desc: "Sbarre, ANPR, EV, serrature smart, IoT e altro" },
    },
    allFeatures: "Vedi tutte le funzionalità", allDetails: "e caratteristiche tecniche →", team: "Team di Sviluppo", teamDesc: "Chi siamo — SeoSoft di Federico Cassani", technology: "Tecnologia", technologyDesc: "Stack tecnico, controller IoT, IoT e protocolli",
  },
  en: {
    features: "Features", prices: "Pricing", blog: "Blog", updates: "Updates", company: "Company", contacts: "Contact", free: "Free consultation", demo: "Request a demo", language: "Language", open: "Open menu", close: "Close menu",
    groups: { "Prenotazioni e ricavi": "Bookings & revenue", "Ospiti e accessi": "Guest arrival & access", "Operatività e integrazioni": "Operations & integrations" },
    items: {
      "Booking Engine": { label: "Booking Engine", desc: "Direct bookings with no commissions" }, "Sincronizzazione iCal": { label: "iCal Synchronization", desc: ".ics feed per pitch — Airbnb, Booking, Google Calendar" }, "Check-in Digitale con AI": { label: "AI Digital Check-in", desc: "Document OCR and magic self check-in link" }, "OVVO Videoverifica": { label: "OVVO Video Verification", desc: "Remote video check-in with a legal report" }, "Alloggiati Web (Questura)": { label: "Alloggiati Web (Police)", desc: "Automatic submission of official guest forms" }, "Check-out con QR Code": { label: "QR Code Check-out", desc: "Automatic barrier and stay summary" }, "Controllo Accessi Sbarra": { label: "Barrier Access Control", desc: "Gate integration and attendance logs" }, "Gestionale Aree Sosta Camper": { label: "RV Park Management", desc: "Pitches, bookings and access" }, "Mappa Interattiva": { label: "Interactive Map", desc: "Real-time pitch status, drag & drop" }, "Prezzi Dinamici e Tasse": { label: "Dynamic Pricing & Taxes", desc: "Seasonality and automatic tourist tax" }, "Gestione Manutenzioni": { label: "Maintenance Management", desc: "Fault reports and pitch blocking" }, "Ruoli e Permessi Staff": { label: "Staff Roles & Permissions", desc: "Separate Admin, Reception and Maintenance roles" }, "Reportistica e Statistiche": { label: "Reports & Statistics", desc: "Occupancy dashboard and daily PDF" }, "Automazione Email": { label: "Email Automation", desc: "Confirmations, reminders and SMTP notifications" }, "Sicurezza e Backup GDPR": { label: "GDPR Security & Backup", desc: "Encryption, MFA and complete JSON export" }, "Integrazioni Personalizzate": { label: "Custom Integrations", desc: "Barriers, ANPR, EV, smart locks, IoT and more" },
    },
    allFeatures: "See all features", allDetails: "and technical details →", team: "Development Team", teamDesc: "About us — SeoSoft by Federico Cassani", technology: "Technology", technologyDesc: "Technical stack, IoT controller, IoT and protocols",
  },
  de: {
    features: "Funktionen", prices: "Preise", blog: "Blog", updates: "Neuigkeiten", company: "Unternehmen", contacts: "Kontakt", free: "Kostenlose Beratung", demo: "Demo anfordern", language: "Sprache", open: "Menü öffnen", close: "Menü schließen",
    groups: { "Prenotazioni e ricavi": "Buchungen & Ertrag", "Ospiti e accessi": "Gäste & Zugang", "Operatività e integrazioni": "Betrieb & Integrationen" },
    items: {
      "Booking Engine": { label: "Buchungsmaschine", desc: "Direktbuchungen ohne Provisionen" }, "Sincronizzazione iCal": { label: "iCal-Synchronisierung", desc: ".ics-Feed pro Stellplatz — Airbnb, Booking, Google Kalender" }, "Check-in Digitale con AI": { label: "Digitaler Check-in mit KI", desc: "Dokumenten-OCR und Self-Check-in-Link" }, "OVVO Videoverifica": { label: "OVVO-Videoverifizierung", desc: "Remote-Check-in per Videoanruf mit rechtlichem Protokoll" }, "Alloggiati Web (Questura)": { label: "Alloggiati Web (Polizei)", desc: "Automatische Übermittlung amtlicher Gästemeldungen" }, "Check-out con QR Code": { label: "Check-out per QR-Code", desc: "Automatische Schranke und Aufenthaltsübersicht" }, "Controllo Accessi Sbarra": { label: "Schrankenzugangskontrolle", desc: "Schrankenintegration und Anwesenheitsprotokolle" }, "Gestionale Aree Sosta Camper": { label: "Software für Wohnmobilstellplätze", desc: "Stellplätze, Buchungen und Zugang" }, "Mappa Interattiva": { label: "Interaktive Karte", desc: "Stellplatzstatus in Echtzeit, Drag & Drop" }, "Prezzi Dinamici e Tasse": { label: "Dynamische Preise & Steuern", desc: "Saisonalität und automatische Kurtaxe" }, "Gestione Manutenzioni": { label: "Wartungsverwaltung", desc: "Störungsmeldungen und Sperrung von Stellplätzen" }, "Ruoli e Permessi Staff": { label: "Mitarbeiterrollen & Rechte", desc: "Getrennte Rollen für Admin, Rezeption und Wartung" }, "Reportistica e Statistiche": { label: "Berichte & Statistiken", desc: "Belegungs-Dashboard und tägliches PDF" }, "Automazione Email": { label: "E-Mail-Automatisierung", desc: "Bestätigungen, Erinnerungen und SMTP-Benachrichtigungen" }, "Sicurezza e Backup GDPR": { label: "DSGVO-Sicherheit & Backup", desc: "Verschlüsselung, MFA und vollständiger JSON-Export" }, "Integrazioni Personalizzate": { label: "Individuelle Integrationen", desc: "Schranken, ANPR, EV, Smart Locks, IoT und mehr" },
    },
    allFeatures: "Alle Funktionen", allDetails: "und technischen Details →", team: "Entwicklungsteam", teamDesc: "Über uns — SeoSoft von Federico Cassani", technology: "Technologie", technologyDesc: "Technischer Stack, IoT-Controller, IoT und Protokolle",
  },
  hr: {
    features: "Funkcionalnosti", prices: "Cijene", blog: "Blog", updates: "Novosti", company: "Tvrtka", contacts: "Kontakt", free: "Besplatno savjetovanje", demo: "Zatražite demonstraciju", language: "Jezik", open: "Otvori izbornik", close: "Zatvori izbornik",
    groups: { "Prenotazioni e ricavi": "Rezervacije i prihodi", "Ospiti e accessi": "Gosti i pristup", "Operatività e integrazioni": "Operativno upravljanje i integracije" },
    items: {
      "Booking Engine": { label: "Booking Engine", desc: "Izravne rezervacije bez provizija" }, "Sincronizzazione iCal": { label: "iCal sinkronizacija", desc: ".ics feed po parceli — Airbnb, Booking, Google Kalendar" }, "Check-in Digitale con AI": { label: "Digitalna prijava uz AI", desc: "OCR dokumenata i poveznica za samostalnu prijavu" }, "OVVO Videoverifica": { label: "OVVO videoprovjera", desc: "Udaljena prijava videopozivom s pravnim zapisnikom" }, "Alloggiati Web (Questura)": { label: "Alloggiati Web (policija)", desc: "Automatsko slanje službenih podataka o gostima" }, "Check-out con QR Code": { label: "Odjava QR kodom", desc: "Automatska rampa i sažetak boravka" }, "Controllo Accessi Sbarra": { label: "Kontrola pristupa rampi", desc: "Integracija ulaza i zapisnik prisutnosti" }, "Gestionale Aree Sosta Camper": { label: "Upravljanje kamp odmorištem", desc: "Parcele, rezervacije i pristup" }, "Mappa Interattiva": { label: "Interaktivna karta", desc: "Status parcela u stvarnom vremenu, povuci i ispusti" }, "Prezzi Dinamici e Tasse": { label: "Dinamične cijene i pristojbe", desc: "Sezonalnost i automatska boravišna pristojba" }, "Gestione Manutenzioni": { label: "Upravljanje održavanjem", desc: "Prijava kvarova i blokiranje parcela" }, "Ruoli e Permessi Staff": { label: "Uloge i dopuštenja osoblja", desc: "Odvojeni administrator, recepcija i održavanje" }, "Reportistica e Statistiche": { label: "Izvještaji i statistika", desc: "Nadzorna ploča popunjenosti i dnevni PDF" }, "Automazione Email": { label: "Automatizacija e-pošte", desc: "Potvrde, podsjetnici i SMTP obavijesti" }, "Sicurezza e Backup GDPR": { label: "GDPR sigurnost i sigurnosna kopija", desc: "Šifriranje, MFA i potpuni JSON izvoz" }, "Integrazioni Personalizzate": { label: "Prilagođene integracije", desc: "Rampe, ANPR, EV, pametne brave, IoT i drugo" },
    },
    allFeatures: "Pogledajte sve funkcionalnosti", allDetails: "i tehničke značajke →", team: "Razvojni tim", teamDesc: "O nama — SeoSoft, Federico Cassani", technology: "Tehnologija", technologyDesc: "Tehnički stog, IoT kontroler, IoT i protokoli",
  },
};

const remoteReceptionNavCopy: Record<Locale, NavItemCopy> = {
  it: { label: "Reception Remota 24/7", desc: "Ingressi, uscite e pratiche ospiti da remoto" },
  en: { label: "24/7 Remote Reception", desc: "Remote guest arrivals, departures and check-in" },
  de: { label: "Remote-Rezeption rund um die Uhr", desc: "Anreisen, Abreisen und Gästemeldungen aus der Ferne" },
  hr: { label: "Udaljena recepcija 24/7", desc: "Udaljena prijava, odjava i evidencija gostiju" },
};

function getFeatureCopy(label: FeatureItem["label"], locale: Locale, items: NavCopy["items"]): NavItemCopy {
  if (label === "Come collegare il gestionale alle sbarre") return {
    it: { label: "Come collegare il gestionale alle sbarre", desc: "Hardware, schema di collegamento e sicurezza" },
    en: { label: "How to connect software to barriers", desc: "Hardware, connection diagram and safety" },
    de: { label: "Software mit Schranken verbinden", desc: "Hardware, Anschlussdiagramm und Sicherheit" },
    hr: { label: "Kako povezati sustav s rampama", desc: "Uređaj, shema povezivanja i sigurnost" },
  }[locale];
  if (label === "Reception Remota 24/7") return remoteReceptionNavCopy[locale];
  if (label === "Gestionale Aree Sosta Camper") return {
    it: { label: "Automazione area sosta", desc: "Il percorso dalla prenotazione all'uscita" },
    en: { label: "RV park automation", desc: "The journey from booking to departure" },
    de: { label: "Stellplatz-Automatisierung", desc: "Der Ablauf von Buchung bis Ausfahrt" },
    hr: { label: "Automatizacija kamp odmorišta", desc: "Postupak od rezervacije do izlaska" },
  }[locale];
  if (label === "Assistente operativo AI") return {
    it: { label: "Assistente operativo AI", desc: "Guida a procedure e configurazioni" },
    en: { label: "AI operations assistant", desc: "Guidance on procedures and settings" },
    de: { label: "KI-Betriebsassistent", desc: "Hilfe bei Abläufen und Einstellungen" },
    hr: { label: "AI operativni asistent", desc: "Pomoć s postupcima i postavkama" },
  }[locale];
  return items[label];
}

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aziendaOpen, setAziendaOpen] = useState(false);
  const [mobileFeatureOpen, setMobileFeatureOpen] = useState(false);
  const [mobileAziendaOpen, setMobileAziendaOpen] = useState(false);
  const [location] = useLocation();
  const availableLocales = availableLocalesForPath(location);
  const { locale, localePath } = useLocale();
  const navCopy = { ...navCopies[locale], features: { it: "Soluzioni", en: "Solutions", de: "Lösungen", hr: "Rješenja" }[locale] };
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const featureMenuRef = useRef<HTMLDivElement>(null);
  const featureTriggerRef = useRef<HTMLButtonElement>(null);
  const aziendaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !featureMenuRef.current?.contains(target)
      ) {
        setDropdownOpen(false);
      }
      if (aziendaRef.current && !aziendaRef.current.contains(e.target as Node)) {
        setAziendaOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setMobileFeatureOpen(false);
    setMobileAziendaOpen(false);
    setDropdownOpen(false);
    setAziendaOpen(false);
  }, [location]);

  const allFeatureRoutes = [...featureGroups.flatMap((g) => g.items.map((i) => i.href.split("#")[0])), "/gestionale-agricampeggi", "/gestionale-glamping"];
  const isFeatureActive = allFeatureRoutes.includes(location) || location === "/funzionalita";
  const isAziendaActive = location === "/team" || location === "/tecnologia";
  const changeLanguage = (target: Locale) => {
    if (!availableLocales.includes(target)) return;
    rememberLanguagePreference(target);
    window.location.href = buildLocalePath(location, target);
  };

  return (
    <header
      className={cn(
        "fixed top-0 w-full z-50 transition-all duration-300 border-b",
        scrolled
          ? "bg-background border-border shadow-sm py-3"
          : "bg-background border-transparent py-5"
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
             <Link href={localePath("/")} className="hover:opacity-90 transition-opacity">
            <Logo variant="dark" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
            {/* Funzionalità dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                ref={featureTriggerRef}
                onClick={() => { setDropdownOpen((v) => !v); setAziendaOpen(false); }}
                onKeyDown={(event) => { if (event.key === "Escape") setDropdownOpen(false); }}
                  className={cn(
                   "flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  isFeatureActive ? "text-primary font-semibold" : "text-muted-foreground"
                )}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
                aria-controls="features-mega-menu"
                data-testid="features-menu-toggle"
              >
                 {navCopy.features}
                <ChevronDown
                  className={cn("w-4 h-4 transition-transform duration-200", dropdownOpen && "rotate-180")}
                />
              </button>

              {/* Il pannello usa la viewport come riferimento per non uscire dallo schermo. */}
              {dropdownOpen && (
                <div
                  id="features-mega-menu"
                  ref={featureMenuRef}
                  data-testid="features-mega-menu"
                  style={{ top: scrolled ? "76px" : "92px" }}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setDropdownOpen(false);
                      featureTriggerRef.current?.focus();
                    }
                  }}
                  className="fixed left-1/2 z-50 hidden max-h-[calc(100dvh-7rem)] w-[min(1280px,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto rounded-2xl border border-border bg-background p-5 shadow-2xl shadow-primary/10 xl:block"
                >
                  <StructureNavigation />
                  <div className="grid grid-cols-3 gap-5">
                    {featureGroups.map((group) => (
                      <section className="min-w-0 rounded-xl border border-primary/10 bg-primary/[0.025] p-3" key={group.label} data-testid="feature-topic">
                        <h2 className="mb-3 flex min-h-12 items-center gap-3 border-b border-primary/10 px-2 pb-3 text-sm font-bold leading-snug text-primary">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                            <group.icon className="h-4 w-4" aria-hidden="true" />
                          </span>
                          {navCopy.groups[group.label]}
                        </h2>
                        <ul className="space-y-0.5">
                          {group.items.map((item) => {
                            const itemCopy = getFeatureCopy(item.label, locale, navCopy.items);

                            return (
                              <li key={item.href}>
                                <Link
                                  href={localePath(item.href)}
                                  title={itemCopy.desc}
                                  className={cn(
                                     "group/item flex min-w-0 items-start gap-3 rounded-xl px-2 py-3 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    location === item.href && "bg-primary/5"
                                  )}
                                >
                                  <span
                                    className={cn(
                                       "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors",
                                      location === item.href
                                        ? "bg-primary text-primary-foreground"
                                        : "bg-primary/10 text-primary group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                                    )}
                                  >
                                    <item.icon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span
                                      className={cn(
                                         "block text-sm font-semibold leading-snug",
                                        location === item.href ? "text-primary" : "text-foreground"
                                      )}
                                    >
                                      {itemCopy.label}
                                    </span>
                                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                                      {itemCopy.desc}
                                    </span>
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    ))}
                  </div>

                  <div className="mt-4 border-t border-border pt-3">
                    <Link
                      href={localePath("/funzionalita")}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-accent"
                    >
                      {navCopy.allFeatures} {navCopy.allDetails}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Prezzi */}
            <Link
              href={localePath("/prezzi")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-accent",
                location === "/prezzi" ? "text-primary font-semibold" : "text-muted-foreground"
              )}
            >
               {navCopy.prices}
            </Link>

            {/* Blog */}
            <Link
              href={localePath("/blog")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-accent",
                location.startsWith("/blog") ? "text-primary font-semibold" : "text-muted-foreground"
              )}
            >
              Blog
            </Link>

            <Link
              href={localePath("/novita")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-accent",
                location === "/novita" ? "text-primary font-semibold" : "text-muted-foreground"
              )}
              data-testid="nav-link-updates"
            >
              {navCopy.updates}
            </Link>

            {/* Azienda dropdown */}
            <div ref={aziendaRef} className="relative">
              <button
                onClick={() => setAziendaOpen((v) => !v)}
                onMouseEnter={() => setAziendaOpen(true)}
                className={cn(
                  "flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-accent focus:outline-none",
                  isAziendaActive ? "text-primary font-semibold" : "text-muted-foreground"
                )}
                aria-expanded={aziendaOpen}
                aria-haspopup="true"
              >
                 {navCopy.company}
                <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", aziendaOpen && "rotate-180")} />
              </button>

              {aziendaOpen && (
                <div
                  onMouseLeave={() => setAziendaOpen(false)}
                  className="absolute top-full right-0 mt-3 w-72 bg-background border border-border rounded-2xl shadow-2xl shadow-primary/10 p-4"
                >
                  <Link
                    href={localePath("/team")}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-xl transition-colors hover:bg-muted group/item",
                      location === "/team" && "bg-primary/5"
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                      location === "/team" ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                    )}>
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <span className={cn("block text-sm font-semibold", location === "/team" ? "text-primary" : "text-foreground")}>
                         {navCopy.team}
                      </span>
                       <span className="block text-xs text-muted-foreground mt-0.5">{navCopy.teamDesc}</span>
                    </div>
                  </Link>
                  <Link
                     href={localePath("/tecnologia")}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-xl transition-colors hover:bg-muted group/item",
                      location === "/tecnologia" && "bg-primary/5"
                    )}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors",
                      location === "/tecnologia" ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary group-hover/item:bg-primary group-hover/item:text-primary-foreground"
                    )}>
                      <Code2 className="w-4 h-4" />
                    </div>
                    <div>
                      <span className={cn("block text-sm font-semibold", location === "/tecnologia" ? "text-primary" : "text-foreground")}>
                         {navCopy.technology}
                      </span>
                       <span className="block text-xs text-muted-foreground mt-0.5">{navCopy.technologyDesc}</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* Contatti */}
            <Link
              href={localePath("/contatti")}
              className={cn(
                "text-sm font-medium transition-colors hover:text-accent",
                location === "/contatti" ? "text-primary font-semibold" : "text-muted-foreground"
              )}
            >
               {navCopy.contacts}
            </Link>
            <DesktopLanguagePicker locale={locale} onChange={changeLanguage} label={navCopy.language} availableLocales={availableLocales} />

            {/* Consulenza telefonica */}
            <button
              onClick={() => window.open(BOOKING_URL, "ovvo-consulenza", `width=900,height=700,left=${Math.round((screen.width-900)/2)},top=${Math.round((screen.height-700)/2)}`)}
              className="hidden 2xl:inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-accent transition-colors cursor-pointer"
            >
              <Phone className="w-3.5 h-3.5" />
               {navCopy.free}
            </button>

            {/* CTA */}
            <button
              onClick={() => window.open(BOOKING_URL, "ovvo-demo", `width=900,height=700,left=${Math.round((screen.width-900)/2)},top=${Math.round((screen.height-700)/2)}`)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:shadow-md hover:-translate-y-0.5 inline-flex items-center cursor-pointer"
            >
               {navCopy.demo}
            </button>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="xl:hidden p-2 text-primary"
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
             aria-label={isOpen ? navCopy.close : navCopy.open}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 top-[72px] flex w-screen max-w-full flex-col gap-2 overflow-x-hidden overflow-y-auto border-t border-border bg-background px-4 pb-24 pt-4 shadow-lg xl:hidden">
          {/* Funzionalità accordion */}
          <button
            onClick={() => setMobileFeatureOpen((v) => !v)}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg text-base font-medium w-full text-left",
              isFeatureActive ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
             {navCopy.features}
            <ChevronDown
              className={cn("w-4 h-4 transition-transform duration-200", mobileFeatureOpen && "rotate-180")}
            />
          </button>

          {mobileFeatureOpen && (
            <div className="pl-2 flex flex-col gap-4 pb-2">
              <StructureNavigation />
              {featureGroups.map((group) => (
                <div key={group.label}>
                  <p className="flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-widest mb-2 px-2">
                     <group.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                     {navCopy.groups[group.label]}
                  </p>
                  {group.items.map((item) => {
                    const itemCopy = getFeatureCopy(item.label, locale, navCopy.items);
                    return (
                    <Link
                      key={item.href}
                       href={localePath(item.href)}
                      data-testid={`mobile-nav-feature-${item.href.replace('/', '')}`}
                      className={cn(
                        "flex items-center gap-3 p-2.5 rounded-lg text-sm font-medium",
                        location === item.href
                          ? "bg-primary/5 text-primary"
                          : "text-foreground hover:bg-muted"
                      )}
                    >
                      <item.icon className="w-4 h-4 text-primary shrink-0" />
                       {itemCopy.label}
                    </Link>
                    );
                  })}
                </div>
              ))}
              <Link
                 href={localePath("/funzionalita")}
                className="text-sm font-semibold text-primary px-2 pt-1"
              >
                  {navCopy.allFeatures} →
              </Link>
            </div>
          )}

          {/* Prezzi */}
          <Link
             href={localePath("/prezzi")}
            className={cn(
              "p-3 rounded-lg text-base font-medium",
              location === "/prezzi" ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
             {navCopy.prices}
          </Link>

          {/* Blog */}
          <Link
             href={localePath("/blog")}
            className={cn(
              "p-3 rounded-lg text-base font-medium",
              location.startsWith("/blog") ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
             {navCopy.blog}
          </Link>

          <Link
            href={localePath("/novita")}
            data-testid="mobile-nav-link-updates"
            className={cn(
              "p-3 rounded-lg text-base font-medium",
              location === "/novita" ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
            {navCopy.updates}
          </Link>

          {/* Azienda accordion mobile */}
          <button
            onClick={() => setMobileAziendaOpen((v) => !v)}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg text-base font-medium w-full text-left",
              isAziendaActive ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
             {navCopy.company}
            <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", mobileAziendaOpen && "rotate-180")} />
          </button>
          {mobileAziendaOpen && (
            <div className="pl-2 flex flex-col gap-1 pb-1">
              <Link
                 href={localePath("/team")}
                className={cn(
                  "flex items-center gap-3 p-2.5 rounded-lg text-sm font-medium",
                  location === "/team" ? "bg-primary/5 text-primary" : "text-foreground hover:bg-muted"
                )}
              >
                 <Users className="w-4 h-4 text-primary shrink-0" /> {navCopy.team}
              </Link>
              <Link
                 href={localePath("/tecnologia")}
                className={cn(
                  "flex items-center gap-3 p-2.5 rounded-lg text-sm font-medium",
                  location === "/tecnologia" ? "bg-primary/5 text-primary" : "text-foreground hover:bg-muted"
                )}
              >
                 <Code2 className="w-4 h-4 text-primary shrink-0" /> {navCopy.technology}
              </Link>
            </div>
          )}

          {/* Contatti */}
          <Link
             href={localePath("/contatti")}
            className={cn(
              "p-3 rounded-lg text-base font-medium",
              location === "/contatti" ? "bg-primary/5 text-primary" : "text-muted-foreground hover:bg-muted"
            )}
          >
             {navCopy.contacts}
          </Link>

          <div className="h-px bg-border my-1" />
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 pt-2">{navCopy.language}</p>
          <div className="grid grid-cols-2 gap-2">
            {availableLocales.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => changeLanguage(item)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border border-border p-3 text-sm font-medium",
                  item === locale ? "bg-primary/10 text-primary border-primary/30" : "bg-muted text-foreground",
                )}
              >
                <CountryFlag locale={item} />
                {LOCALE_LABELS[item]}
              </button>
            ))}
          </div>

          {/* Consulenza telefonica mobile */}
          <button
            onClick={() => { setIsOpen(false); window.open(BOOKING_URL, "ovvo-consulenza", `width=900,height=700,left=${Math.round((screen.width-900)/2)},top=${Math.round((screen.height-700)/2)}`); }}
            className="flex items-center justify-center gap-2 border-2 border-primary/30 text-primary py-3 rounded-lg font-semibold text-sm w-full cursor-pointer"
          >
            <Phone className="w-4 h-4" />
             {navCopy.free}
          </button>

          {/* CTA */}
          <button
            onClick={() => { setIsOpen(false); window.open(BOOKING_URL, "ovvo-demo", `width=900,height=700,left=${Math.round((screen.width-900)/2)},top=${Math.round((screen.height-700)/2)}`); }}
            className="bg-primary text-primary-foreground text-center py-3 rounded-lg font-semibold w-full cursor-pointer"
          >
             {navCopy.demo}
          </button>
        </div>
      )}
    </header>
  );
}
